import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ACADEMY_LOGO, ACADEMY_LOGO_H, IMAGES, COACH_IMG, SECTION_BG_WHY, SECTION_BG_PRICING } from "../data/assets";
import { BOOK } from "../data/chapters";
import { HOW_IT_WORKS, SKILL_TIERS } from "../data/system";
import { usePlayerAuth } from "../context/PlayerAuthContext";
import AuthDialog from "../components/AuthDialog";
import RedeemDialog from "../components/RedeemDialog";
import LiveLeaderboardPreview from "../components/landing/LiveLeaderboardPreview";
import PlayerOfWeek from "../components/PlayerOfWeek";
import { youtubeEmbed } from "../lib/video";
import LabShowcase, { DEMO_PLAYER, DEMO_ENGINE, DEMO_TICKER } from "../components/landing/LabShowcase";
import SkeletalHudMock from "../components/landing/SkeletalHudMock";
import { ElitePlayerCard } from "../components/engine/ElitePlayerCard";
import { TickerStrip } from "../components/elite/TechnicalTicker";
import { MobileFoldRow, MobileStickyCta } from "../components/landing/MobileFold";
import HeroFilm from "../components/landing/HeroFilm";
import { captureUtm, track } from "../lib/utm";
import { TestimonialSection } from "../components/landing/TestimonialSection";
import { FamilySwitchMock } from "../components/landing/FamilySwitchMock";
import {
  Rocket, ShoppingCart, LogIn, Mail, Trophy, Medal, Video, CheckCircle2, Sparkles,
  BarChart3, PlayCircle, Flame, Star, ArrowRight, Lock, Crown, Check, Calculator, TrendingUp, Ticket, GraduationCap, Users, HelpCircle, ChevronDown, Volume2, VolumeX, ScanLine, BookOpen, UserPlus, ClipboardList, Upload, BadgeCheck,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SNEAK_VIDEO = "https://youtu.be/sb-o-ZBVeto";
// Confirmed Go Live date: September 1, 2026.
const LAUNCH_DATE = new Date("2026-09-01T00:00:00");
// Annual billing toggle is hidden until annual Stripe prices exist (monthly only at launch).
const SHOW_ANNUAL_TOGGLE = false;

const TIERS = [
  {
    id: "ebook", name: "Ball Mastery Blueprint", price: "49", per: "one-time",
    tagline: "Volume 1 — build an elite technical foundation.",
    inherits: null, savings: null, kicker: "Tier 1 · Blueprint",
    features: ["12-Chapter Animated Curriculum", "Gamified Player Hub & Central Illinois Leaderboard", "Weak-Foot Progress Charts", "1 Free Coach Video Review"],
    cta: "Enroll in the Blueprint", ctaType: "buy", highlight: false,
  },
  {
    id: "academy", name: "The Technical Track", price: "29", per: "/month",
    annualPrice: "290", annualPer: "/year", annualNote: "2 months free",
    tagline: "Daily coaching that pays for itself.",
    inherits: "Everything in the Blueprint", savings: "10%", kicker: "Tier 2 · Coaching",
    features: ["Unlimited Video Uploads", "Personal Voice Feedback from Coach Barker", "Weekly Training Sessions & Monthly Roadmap", "10% OFF private training, group sessions & merch"],
    cta: "Enroll in the Technical Track", ctaType: "buy", highlight: false,
  },
  {
    id: "elite", name: "Elite Coaching", price: "49", per: "/month",
    annualPrice: "490", annualPer: "/year", annualNote: "2 months free",
    tagline: "The Technical Academy's lab tier — sports-lab tech for your player.",
    inherits: "Everything in the Technical Track", savings: "20%", badge: "Best Value", kicker: "Tier 3 · Technical Academy Lab",
    lab: [
      "Next O Technical Engine — proprietary video scan on every upload",
      "Freeze-Frame Skeletal HUD & slow-mo fix clips",
      "Animated Elite Player Card, minted every scan",
      "Private Locker Room — Elite-only coach video feed",
      "Live Technical Ticker — reps, streaks & personal bests",
      "Pro-Scout radar charts & transformation trend lines",
    ],
    features: ["Personal video messages from Coach Barker", "Priority Leaderboard Ranking", "20% OFF private training, group sessions & merch", "Early Registration & Exclusive Giveaways"],
    cta: "Enroll in Elite Coaching", ctaType: "buy", highlight: true,
  },
];

const getRemaining = () => {
  const diff = LAUNCH_DATE.getTime() - Date.now();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, done: true };
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff / 3600000) % 24),
    minutes: Math.floor((diff / 60000) % 60),
    seconds: Math.floor((diff / 1000) % 60),
    done: false,
  };
};

const stagger = { animate: { transition: { staggerChildren: 0.08 } } };
const rise = { initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } };

const TIER_LABEL = { ebook: "Volume 1: Ball Mastery Blueprint", academy: "The Technical Track", elite: "Elite Coaching" };

// Storyboard carousel frames (cinematic key-frames for the "See It In Action" section)
const KEYFRAMES = [
  { src: "/marketing/kf1.jpg", phase: "The Hook", caption: "Stop guessing — start training with a real system." },
  { src: "/marketing/kf2.jpg", phase: "Rep Your City", caption: "Climb the Central Illinois leaderboard for your club & school." },
  { src: "/marketing/kf3.jpg", phase: "Real Coach Feedback", caption: "Upload a rep, get a personal voice note from Coach Barker." },
  { src: "/marketing/kf4.jpg", phase: "Train Like a Pro", caption: "Anatomy body maps & team readiness — sports-science built in." },
  { src: "/marketing/kf5.jpg", phase: "Start Your Journey", caption: "Three tiers, one platform for the whole family." },
];

const FAQS = [
  { q: "How do I upload a video for coach feedback?", a: "Once you're in the Player Hub, scroll to \"My Video Proofs,\" tap Choose Video, film a short 10–20 second clip of your move (whole body in frame, ball visible, good lighting), name the move, and upload. Coach Barker reviews it and sends you feedback — including a personal voice note — right inside the app." },
  { q: "Does the Blueprint work for all ages?", a: "Yes. The Technical Academy curriculum is built as a progression, so it works for players roughly ages 7 and up — from beginners nailing their first touches to advanced players sharpening elite moves. Younger kids simply start at the Foundations tier and level up at their own pace." },
  { q: "How do sibling / family discounts work?", a: "One Blueprint enrollment covers up to 5 players in your household. The first coaching enrollment pays full price, and each additional sibling on the same track is automatically discounted at checkout — no codes needed. Every child gets their own profile, points, jersey and leaderboard rank, all shared under one account." },
  { q: "How do I join the Central Illinois leaderboard?", a: "Just set your town and club/school in your player profile. Anyone in a Central Illinois town is automatically entered on the regional leaderboard, where you earn points for chapters, drills, streaks and coach-approved videos — and rep your club and school against the rest of the area." },
];

const LEADERS_MEDAL = { 1: "#F5C518", 2: "#C9CBD1", 3: "#CD7F32" };
const MEDAL_COLORS = LEADERS_MEDAL;

export default function ComingSoon() {
  const navigate = useNavigate();
  const { buy, player } = usePlayerAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [showRedeem, setShowRedeem] = useState(false);
  const [redeemCode, setRedeemCode] = useState("");
  const [email, setEmail] = useState("");
  const [buying, setBuying] = useState(false);
  const [pricing, setPricing] = useState({ retail: 49, early: 39 });
  const [remaining, setRemaining] = useState(getRemaining());
  const [scrolled, setScrolled] = useState(false);
  const [folds, setFolds] = useState({});
  const toggleFold = (id) => setFolds((f) => ({ ...f, [id]: !f[id] }));
  const fold = (id) => (folds[id] ? "open" : "closed");
  const tourVideoRef = useRef(null);
  const [tourMuted, setTourMuted] = useState(true);
  const tourOverrideRef = useRef(false); // set once the user taps the sound toggle

  // Auto-unmute the tour when it scrolls fully into view, gently fade the VO in,
  // and mute again when it leaves — unless the user has taken manual control.
  useEffect(() => {
    const v = tourVideoRef.current;
    if (!v) return;
    let fade;
    const fadeTo = (target, onDone) => {
      clearInterval(fade);
      fade = setInterval(() => {
        const step = 0.08;
        let nv = v.volume;
        if (Math.abs(nv - target) <= step) {
          v.volume = target;
          clearInterval(fade);
          onDone && onDone();
        } else {
          nv += nv < target ? step : -step;
          v.volume = Math.max(0, Math.min(1, nv));
        }
      }, 45);
    };
    const io = new IntersectionObserver((entries) => {
      if (tourOverrideRef.current) return;
      const e = entries[0];
      if (e.isIntersecting && e.intersectionRatio >= 0.6) {
        v.muted = false;
        v.volume = 0;
        setTourMuted(false);
        v.play?.().catch(() => {});
        fadeTo(1);
      } else {
        fadeTo(0, () => { v.muted = true; setTourMuted(true); });
      }
    }, { threshold: [0, 0.6, 1] });
    io.observe(v);
    return () => { io.disconnect(); clearInterval(fade); };
  }, []);
  const [billing, setBilling] = useState("monthly"); // monthly | annual
  const [sessions, setSessions] = useState(4); // private sessions/month for savings calc
  const [faqOpen, setFaqOpen] = useState(0);
  const [slide, setSlide] = useState(0);
  const embed = youtubeEmbed(SNEAK_VIDEO);

  // Auto-advance the storyboard carousel.
  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % KEYFRAMES.length), 3800);
    return () => clearInterval(t);
  }, []);

  const SESSION_PRICE = 65; // avg private-session cost ($50–$80)
  const monthlySpend = sessions * SESSION_PRICE;
  const academyMonthlySave = Math.round(monthlySpend * 0.10);
  const eliteMonthlySave = Math.round(monthlySpend * 0.20);
  const eliteNetYear = eliteMonthlySave * 12 - 490;
  const academyNetYear = academyMonthlySave * 12 - 290;

  useEffect(() => {
    axios.get(`${API}/pricing`).then(({ data }) => setPricing(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setInterval(() => setRemaining(getRemaining()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    captureUtm();
    track("landing_view", { once: true });
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const getEarlyAccess = async () => {
    setBuying(true);
    try { await buy(false); } // always the direct $49 eBook checkout
    catch (err) {
      setBuying(false);
      toast.error(err?.response?.data?.detail || "Could not start checkout. Please try again in a moment.");
    }
  };
  const earlyOpen = false; // Hub is live — direct $49 eBook checkout, no early-access/waitlist
  const membershipsOpen = pricing.memberships_open === true;

  const buyMembership = async (tier) => {
    setBuying(true);
    try { await buy(false, tier); }
    catch (err) {
      setBuying(false);
      toast.error(err?.response?.data?.detail || "Could not start checkout. Please try again in a moment.");
    }
  };

  const scrollToTiers = () => document.querySelector("[data-testid='membership-tiers-section']")?.scrollIntoView({ behavior: "smooth" });

  // Deep links: ?action=buy-ebook | join-academy | join-elite  and/or  ?tier=ebook|academy|elite  (+ optional ?email=)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const action = params.get("action");
    const tier = params.get("tier");
    const preEmail = params.get("email");
    if (preEmail) setEmail(preEmail);
    else if (player?.email) setEmail(player.email);

    const redeem = params.get("redeem");
    if (redeem !== null) {
      setRedeemCode(redeem || "");
      const t = setTimeout(() => setShowRedeem(true), 450);
      return () => clearTimeout(t);
    }

    if (action === "buy-ebook") {
      const t = setTimeout(() => getEarlyAccess(), 450);
      return () => clearTimeout(t);
    }
    if (action === "join-academy" || tier === "academy" || action === "join-elite" || tier === "elite") {
      const t = setTimeout(() => scrollToTiers(), 500);
      return () => clearTimeout(t);
    }
    if (tier === "ebook") {
      const t = setTimeout(() => document.querySelector("[data-testid='membership-tiers-section']")?.scrollIntoView({ behavior: "smooth" }), 500);
      return () => clearTimeout(t);
    }
  }, [player]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white overflow-x-hidden landing-sticky-pad">
      <MobileStickyCta label={buying ? "Redirecting…" : `Get the Blueprint — $${pricing.retail}`} onClick={getEarlyAccess} disabled={buying} />
      {/* STICKY GLASS HEADER */}
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 safe-top transition-all duration-300 ${scrolled ? "backdrop-blur-xl bg-black/70 border-b border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.5)]" : "bg-transparent border-b border-transparent"}`}
      >
        <div className="max-w-6xl mx-auto px-6 sm:px-10 flex items-center justify-between h-16">
          <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center" data-testid="header-logo">
            <img src={ACADEMY_LOGO_H} alt="The Next O Technical Academy" className="h-12 sm:h-14 w-auto object-contain" />
          </button>
          <div className="flex items-center gap-1 sm:gap-2">
            <button data-testid="header-system-btn" onClick={() => navigate("/the-system")}
              className="hidden md:inline-flex items-center gap-1.5 text-white/70 hover:text-white font-subheadings text-xs font-bold uppercase tracking-wider px-3 py-2 transition-colors">
              <BookOpen size={14} /> The System
            </button>
            <button data-testid="header-for-schools-btn" onClick={() => navigate("/for-schools")}
              className="hidden md:inline-flex items-center gap-1.5 text-white/70 hover:text-white font-subheadings text-xs font-bold uppercase tracking-wider px-3 py-2 transition-colors">
              <GraduationCap size={14} /> For Schools
            </button>
            <button data-testid="header-redeem-btn" onClick={() => { setRedeemCode(""); setShowRedeem(true); }}
              className="hidden sm:inline-flex items-center gap-1.5 text-[#D4AF37] hover:text-[#e8c14b] font-subheadings text-xs font-bold uppercase tracking-wider px-3 py-2 transition-colors">
              <Ticket size={14} /> Team Code
            </button>
            <button data-testid="header-login-btn" onClick={() => navigate("/login")}
              className="inline-flex items-center gap-1.5 whitespace-nowrap text-white/70 hover:text-white font-subheadings text-xs font-bold uppercase tracking-wider px-2.5 sm:px-3 py-2.5 transition-colors">
              <LogIn size={14} /> Log in
            </button>
            <button data-testid="header-buy-btn" onClick={getEarlyAccess} disabled={buying}
              className="inline-flex items-center gap-1.5 whitespace-nowrap bg-[#C41E24] hover:bg-[#A0161D] text-white font-subheadings text-xs font-bold uppercase tracking-wider px-3 sm:px-4 py-2.5 rounded-sm transition-colors disabled:opacity-70">
              <ShoppingCart size={14} /> Enroll Now
            </button>
          </div>
        </div>
      </header>

      {/* HERO — high-tech sports lab */}
      <section className="relative min-h-screen flex items-center grain elite-carbon-bg overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <HeroFilm />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/85 to-[#0a0a0a]/35 pointer-events-none" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-[#0a0a0a]/70 pointer-events-none" />
          <div aria-hidden className="absolute inset-0 elite-mesh-overlay pointer-events-none" />
          <div aria-hidden className="absolute inset-0" style={{ background: "radial-gradient(900px 520px at 85% 20%, rgba(212,175,55,0.16), transparent 62%)" }} />
          <div aria-hidden className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "repeating-linear-gradient(0deg, rgba(255,255,255,0.5) 0 1px, transparent 1px 3px)" }} />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 w-full pt-24 sm:pt-28 pb-16 grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-10 items-center">
        <motion.div variants={stagger} initial="initial" animate="animate">
          <motion.div variants={rise} className="mb-6">
            <img data-testid="hero-academy-lockup" src={ACADEMY_LOGO} alt="The Next O Technical Academy — official seal" className="w-40 sm:w-52 h-auto object-contain drop-shadow-[0_12px_40px_rgba(212,175,55,0.35)]" />
          </motion.div>
          <motion.div variants={rise} className="inline-flex items-center gap-2 border border-[#D4AF37]/50 bg-[#D4AF37]/10 px-3 py-1.5 mb-6 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-pulse" style={{ boxShadow: "0 0 8px #D4AF37" }} />
            <span className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-[#D4AF37]">{remaining.done ? "Technical Academy · Now Live" : "Technical Academy · Coming Soon"}</span>
          </motion.div>

          <motion.h1 variants={rise} className="font-cover text-6xl sm:text-8xl lg:text-7xl xl:text-8xl uppercase leading-[0.85] tracking-tight max-w-4xl hero-title-shadow">
            The Next O <span className="text-[#C41E24]">Technical</span> Academy
          </motion.h1>

          <motion.p variants={rise} data-testid="hero-explainer" className="text-lg sm:text-xl text-white/85 mt-6 max-w-2xl leading-relaxed">
            A structured training system where players upload weekly video proof, earn coach feedback, and track their development from Bronze to Elite.
          </motion.p>

          <motion.div variants={rise} className="flex items-center gap-3 mt-5">
            <span className="h-px w-10 bg-[#C41E24]" />
            <span className="font-subheadings text-base sm:text-xl font-bold uppercase tracking-[0.2em]">{BOOK.blueprint}</span>
          </motion.div>

          <motion.p variants={rise} className="font-subheadings text-lg sm:text-2xl font-semibold text-white/80 mt-5 max-w-xl">
            {BOOK.tagline}
          </motion.p>

          <motion.button variants={rise} onClick={() => document.querySelector("[data-testid='family-ready-section']")?.scrollIntoView({ behavior: "smooth" })}
            data-testid="hero-family-pill"
            className="group inline-flex items-center gap-2 mt-5 bg-white/5 hover:bg-white/10 border border-[#D4AF37]/40 hover:border-[#D4AF37] rounded-full pl-2 pr-4 py-1.5 transition-colors">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#D4AF37] text-black"><Users size={13} /></span>
            <span className="font-subheadings text-xs sm:text-sm font-bold uppercase tracking-wider text-[#D4AF37]">One Blueprint · Up to 5 Players</span>
            <ArrowRight size={13} className="text-[#D4AF37] group-hover:translate-x-0.5 transition-transform" />
          </motion.button>

          {/* Countdown (pre-launch) OR live CTA (post-launch) */}
          <motion.div variants={rise} className="mt-8" data-testid="countdown-timer">
            {remaining.done ? (
              <div data-testid="launched-cta">
                <div className="inline-flex items-center gap-2 bg-[#22C55E]/15 border border-[#22C55E]/40 text-[#22C55E] px-3 py-1.5 rounded-full mb-4">
                  <span className="w-2 h-2 rounded-full bg-[#22C55E] animate-pulse" />
                  <span className="text-[11px] font-bold uppercase tracking-[0.25em]">Now Live</span>
                </div>
                <div className="font-subheadings text-lg sm:text-xl font-semibold text-white/85 mb-4">The system is here — start training today.</div>
                <div className="flex flex-wrap items-center gap-3">
                  <button data-testid="launched-buy-btn" onClick={getEarlyAccess} disabled={buying}
                    className="group flex items-center gap-2 bg-[#C41E24] hover:bg-[#A0161D] text-white font-subheadings font-bold uppercase tracking-wider py-3.5 px-7 rounded-sm transition-colors disabled:opacity-70 text-base">
                    <ShoppingCart size={18} /> {buying ? "Redirecting…" : `Start Training — $${pricing.retail}`}
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                  <button data-testid="launched-login-btn" onClick={() => navigate("/login")}
                    className="flex items-center gap-2 text-white/70 hover:text-white font-subheadings font-bold uppercase tracking-wider py-3.5 text-sm transition-colors">
                    <LogIn size={16} className="text-[#C41E24]" /> Already own it? Log in
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-white/45 mb-3">Launching In</div>
                <div className="flex gap-3 sm:gap-4">
                  {[
                    { label: "Days", value: remaining.days },
                    { label: "Hrs", value: remaining.hours },
                    { label: "Min", value: remaining.minutes },
                    { label: "Sec", value: remaining.seconds },
                  ].map((u) => (
                    <div key={u.label} data-testid={`countdown-${u.label.toLowerCase()}`}
                      className="w-16 sm:w-20 bg-[#141414]/80 backdrop-blur border border-white/10 rounded-lg py-3 text-center">
                      <div className="font-cover text-3xl sm:text-4xl text-[#C41E24] leading-none tabular-nums">{String(u.value).padStart(2, "0")}</div>
                      <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-white/40 mt-1.5">{u.label}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </motion.div>

          <motion.div variants={rise} className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2">
            <button data-testid="redeem-code-btn" onClick={() => { setRedeemCode(""); setShowRedeem(true); }}
              className="inline-flex items-center gap-2 border border-[#D4AF37]/50 hover:border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 font-subheadings font-bold uppercase tracking-wider text-xs sm:text-sm py-2.5 px-4 rounded-sm transition-colors">
              <Ticket size={15} /> Club or School Player? Redeem Your Team Code
            </button>
            <button data-testid="hero-learn-system" onClick={() => navigate("/the-system")} className="inline-flex items-center gap-1.5 text-white/60 hover:text-white font-subheadings font-bold uppercase tracking-wider text-xs sm:text-sm transition-colors">
              <BookOpen size={15} className="text-[#D4AF37]" /> Learn how the system works <ArrowRight size={13} />
            </button>
          </motion.div>
        </motion.div>

        {/* HUD stack — live components, not screenshots */}
        <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.9, delay: 0.3, ease: "easeOut" }}
          className="relative hidden lg:block min-w-0" data-testid="hero-hud-stack">
          <div className="absolute -inset-10 rounded-[3rem] pointer-events-none" style={{ background: "radial-gradient(circle at 50% 45%, rgba(212,175,55,0.22), transparent 66%)" }} />
          <div className="relative w-[340px] ml-auto mr-6">
            <SkeletalHudMock />
            <motion.div animate={{ y: [0, -8, 0] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -left-36 -bottom-4 w-[290px] rounded-2xl border border-[#D4AF37]/30 bg-[#0b0b0b]/90 backdrop-blur-xl p-3 elite-card-halo" style={{ transform: "rotate(-4deg)" }}>
              <div className="origin-top-left" style={{ transform: "scale(0.6)", width: 440, height: 192 }}>
                <ElitePlayerCard player={DEMO_PLAYER} engine={DEMO_ENGINE} drill="Elastico · Scan 07" api="" animated />
              </div>
            </motion.div>
          </div>
          <div className="mt-6 rounded-xl border border-[#D4AF37]/40 overflow-hidden bg-[#070707]/95" style={{ boxShadow: "0 0 22px rgba(212,175,55,0.2)" }}>
            <div className="flex items-stretch h-9">
              <div className="flex items-center gap-1.5 px-3 shrink-0 border-r border-[#D4AF37]/30 bg-[#D4AF37]/10"><span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-pulse" /><span className="font-mono text-[9px] font-bold tracking-[0.3em] uppercase text-[#D4AF37]">Live</span></div>
              <TickerStrip items={DEMO_TICKER} className="flex-1" speed="26s" />
            </div>
          </div>
        </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS — 5-step flow (always visible, straight after the hero) */}
      <section className="relative py-16 sm:py-20 px-6 sm:px-10 bg-[#0d0d0d] border-y border-white/10 overflow-hidden" data-testid="how-it-works-section">
        <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(700px 300px at 15% 0%, rgba(196,30,36,0.12), transparent 60%)" }} />
        <div className="relative max-w-6xl mx-auto">
          <div className="inline-flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.35em] text-[#D4AF37] mb-3"><Sparkles size={13} /> How It Works</div>
          <h2 className="font-cover text-4xl sm:text-6xl uppercase tracking-tight leading-[0.9] mb-3">Five steps. <span className="text-[#C41E24]">Every week.</span></h2>
          <p className="text-white/60 max-w-2xl mb-10 text-base sm:text-lg">Train at home and at sessions, prove it on video, get coached, level up.</p>
          <ol className="grid grid-cols-1 md:grid-cols-5 gap-3 sm:gap-4" data-testid="how-it-works-steps">
            {HOW_IT_WORKS.map((st, i) => (
              <motion.li key={st.title} data-testid={`how-step-${i + 1}`} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45, delay: i * 0.07 }}
                className="relative lift-card rounded-2xl border p-5 flex md:flex-col gap-4 md:gap-0 overflow-hidden" style={{ borderColor: i === 4 ? "rgba(212,175,55,0.6)" : "rgba(255,255,255,0.1)", background: i === 4 ? "linear-gradient(180deg, rgba(212,175,55,0.12), #111)" : "rgba(17,17,17,0.9)" }}>
                <span aria-hidden className="absolute right-3 top-1 font-cover text-6xl pointer-events-none" style={{ color: "rgba(212,175,55,0.1)" }}>{i + 1}</span>
                <span className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 text-black" style={{ background: i === 4 ? "linear-gradient(135deg,#F0D77A,#D4AF37 60%,#B8912E)" : "linear-gradient(160deg,#e03a40,#C41E24)", color: i === 4 ? "#000" : "#fff", boxShadow: `0 8px 24px ${i === 4 ? "rgba(212,175,55,0.35)" : "rgba(196,30,36,0.35)"}` }}><st.icon size={20} /></span>
                <div className="min-w-0 md:mt-4">
                  <div className="font-mono text-[9px] font-bold uppercase tracking-[0.3em] text-[#D4AF37]">Step {i + 1}</div>
                  <div className="font-headings text-lg uppercase tracking-wide leading-tight mt-1">{st.title}</div>
                  <p className="text-sm text-white/65 mt-1.5 leading-snug">{st.text}</p>
                </div>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      {/* TIER SYSTEM — Bronze → Elite progression path */}
      <section className="relative py-16 sm:py-20 px-6 sm:px-10 bg-[#0a0a0a] overflow-hidden" data-testid="tier-system-section">
        <div aria-hidden className="absolute inset-0 elite-mesh-overlay pointer-events-none opacity-60" />
        <div className="relative max-w-6xl mx-auto">
          <div className="inline-flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.35em] text-[#D4AF37] mb-3"><Medal size={13} /> The Tier System</div>
          <h2 className="font-cover text-4xl sm:text-6xl uppercase tracking-tight leading-[0.9] mb-3">Where you start. <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F5E7B8] via-[#D4AF37] to-[#B8912E]">Where you can go.</span></h2>
          <p className="text-white/60 max-w-2xl mb-12 text-base sm:text-lg">Your baseline video sets your starting tier. Weekly video proof and coach reviews move you up the path.</p>
          <div className="relative" data-testid="tier-path">
            <div aria-hidden className="hidden md:block absolute left-[8%] right-[8%] top-7 h-1 rounded-full" style={{ background: "linear-gradient(90deg, #CD7F32, #C0C0C0 36%, #D4AF37 68%, #C41E24)", boxShadow: "0 0 18px rgba(212,175,55,0.35)" }} />
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 sm:gap-5">
              {SKILL_TIERS.map((t, i) => (
                <motion.div key={t.name} data-testid={`skill-tier-${t.name.toLowerCase()}`} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45, delay: i * 0.08 }} className="relative flex md:flex-col items-start md:items-center gap-4 md:gap-0 md:text-center">
                  <span className="relative z-10 w-14 h-14 rounded-full flex items-center justify-center font-cover text-xl shrink-0 border-4 border-[#0a0a0a]" style={{ background: t.color, color: t.ink, boxShadow: `0 0 0 2px ${t.color}, 0 0 26px ${t.color}66` }}>{t.name[0]}</span>
                  <div className={`md:mt-5 lift-card rounded-2xl border p-5 flex-1 w-full ${i === 3 ? "border-[#C41E24]/60 bg-gradient-to-b from-[#C41E24]/15 to-[#111]" : "border-white/10 bg-[#111]/90"}`}>
                    <div className="font-mono text-[9px] font-bold uppercase tracking-[0.3em]" style={{ color: t.color }}>{i === 0 ? "Starting point" : i === 3 ? "The goal" : `Tier ${i + 1}`}</div>
                    <div className="font-headings text-2xl uppercase tracking-wide mt-1" style={{ color: i === 3 ? "#fff" : t.color }}>{t.name}</div>
                    <p className="text-sm text-white/70 mt-2 leading-snug">{t.short}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
          <div className="mt-8 flex items-center gap-3 text-xs sm:text-sm text-white/50"><ArrowRight size={15} className="text-[#D4AF37]" /> Every player, every age, starts with the same baseline video — then the path is theirs to climb.</div>
        </div>
      </section>

      {/* MEMBERSHIP TIERS — Digital Academy (moved up: visible right after the hero + trust bar) */}
      <section className="relative py-24 px-6 sm:px-10 border-y border-[#D4AF37]/20 overflow-hidden elite-carbon-bg" data-testid="membership-tiers-section">
        <img loading="lazy" decoding="async" src={SECTION_BG_PRICING} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20 object-bottom" />
        <div className="absolute inset-0 bg-[#0a0a0a]/80" />
        <div aria-hidden className="absolute inset-0 elite-mesh-overlay pointer-events-none" />
        <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(800px 500px at 70% 10%, rgba(212,175,55,0.16), transparent 62%)" }} />
        <div className="relative max-w-6xl mx-auto">
          <div className="inline-flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.35em] text-[#D4AF37] mb-4"><Lock size={13} /> Choose Your Enrollment Level</div>
          <h2 className="font-cover text-5xl sm:text-6xl uppercase tracking-tight leading-[0.9] mb-4">Academy <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F5E7B8] via-[#D4AF37] to-[#B8912E]">Tiers</span></h2>
          <p className="text-white/60 max-w-2xl mb-5 text-lg">From the one-time Ball Mastery Blueprint to the full Technical Academy — proprietary technical analysis, skeletal HUD, animated player cards and a private coach feed. Enrollment pays for itself through real savings on the training you're already paying for.</p>
          <div className="inline-flex items-center gap-2 bg-[#22C55E]/12 border border-[#22C55E]/30 text-[#22C55E] rounded-lg px-4 py-2.5 mb-14 text-sm font-semibold">
            <Sparkles size={16} /> Save up to <span className="font-cover text-lg leading-none">20%</span> on private training, group sessions &amp; merch — every single month.
          </div>

          {SHOW_ANNUAL_TOGGLE && <div data-testid="billing-toggle" className="inline-flex items-center gap-1 bg-[#141414] border border-white/10 rounded-full p-1 mb-12">
            <button data-testid="billing-monthly" onClick={() => setBilling("monthly")}
              className={`px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${billing === "monthly" ? "bg-[#C41E24] text-white" : "text-white/50 hover:text-white"}`}>
              Monthly
            </button>
            <button data-testid="billing-annual" onClick={() => setBilling("annual")}
              className={`flex items-center gap-2 px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${billing === "annual" ? "bg-[#C41E24] text-white" : "text-white/50 hover:text-white"}`}>
              Annual <span className="text-[9px] bg-[#22C55E]/20 text-[#22C55E] px-1.5 py-0.5 rounded-full">Save 17%</span>
            </button>
          </div>}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
            {TIERS.map((t, i) => {
              const isGold = t.id === "elite";
              return (
              <motion.div
                key={t.id}
                data-testid={`tier-${t.id}`}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`relative flex flex-col rounded-2xl p-7 ${isGold ? "border-2 border-[#D4AF37]/70 bg-gradient-to-b from-[#D4AF37]/14 to-[#0d0d0d] elite-card-halo lg:-mt-3 lg:mb-3" : "border border-white/10 bg-[#111]/85 backdrop-blur-xl"}`}
              >
                {t.badge && (
                  <span data-testid={`tier-${t.id}-badge`} className={`absolute -top-3.5 left-1/2 -translate-x-1/2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em] px-4 py-1.5 rounded-full shadow-lg whitespace-nowrap ${isGold ? "bg-gradient-to-r from-[#E7C64B] to-[#C39A2B] text-black" : "bg-[#C41E24] text-white"}`} style={isGold ? { boxShadow: "0 0 22px rgba(212,175,55,0.6)" } : undefined}>
                    <Crown size={13} /> {t.badge}
                  </span>
                )}

                <div className="font-mono text-[9px] font-bold uppercase tracking-[0.3em] mt-1" style={{ color: isGold ? "#D4AF37" : "rgba(255,255,255,0.4)" }}>{t.kicker}</div>
                <h3 className="font-headings text-2xl uppercase tracking-wide mt-1">{t.name}</h3>
                {t.id === "ebook" && earlyOpen ? (
                  <div className="flex items-end gap-2 mt-3">
                    <span className="font-cover text-5xl text-[#C41E24] leading-none">${pricing.early}</span>
                    <span className="font-cover text-2xl text-white/30 line-through leading-none mb-1">${pricing.retail}</span>
                    <span className="text-white/45 text-sm mb-1.5">one-time</span>
                  </div>
                ) : (
                  <div className="mt-3">
                    <div className="flex items-end gap-1.5">
                      <span className="font-cover text-5xl text-white leading-none" data-testid={`tier-${t.id}-price`}>${t.id === "ebook" ? pricing.retail : (billing === "annual" ? t.annualPrice : t.price)}</span>
                      <span className="text-white/45 text-sm mb-1.5">{t.id === "ebook" ? t.per : (billing === "annual" ? t.annualPer : t.per)}</span>
                    </div>
                    {t.id !== "ebook" && billing === "annual" && t.annualNote && (
                      <span data-testid={`tier-${t.id}-annual-note`} className="inline-flex items-center gap-1 mt-2 text-[11px] font-bold uppercase tracking-wider text-[#22C55E] bg-[#22C55E]/12 border border-[#22C55E]/30 rounded-full px-2.5 py-1">
                        <Sparkles size={11} /> {t.annualNote} · ${(t.price * 12 - t.annualPrice)} saved
                      </span>
                    )}
                  </div>
                )}
                <p className="text-sm text-white/60 mt-3 min-h-[40px]">{t.tagline}</p>

                {/* ROI / savings highlight */}
                {t.savings && (
                  <div data-testid={`tier-${t.id}-savings`} className="mt-5 flex items-center gap-3 rounded-xl border border-[#22C55E]/40 bg-[#22C55E]/10 px-4 py-3">
                    <span className="font-cover text-3xl text-[#22C55E] leading-none">{t.savings}</span>
                    <span className="text-xs font-semibold text-[#22C55E]/90 uppercase tracking-wider leading-tight">Off private training,<br />group sessions &amp; merch</span>
                  </div>
                )}
                {t.id === "ebook" && earlyOpen && (
                  <div data-testid="tier-ebook-early" className="mt-5 flex items-center gap-3 rounded-xl border border-[#C41E24]/50 bg-[#C41E24]/12 px-4 py-3">
                    <span className="font-cover text-3xl text-[#C41E24] leading-none">-${(pricing.retail - pricing.early).toFixed(0)}</span>
                    <span className="text-xs font-semibold text-white/85 uppercase tracking-wider leading-tight">Early Access price<br />Ends Sept 1, 2026</span>
                  </div>
                )}

                <div className="mt-6 flex-1">
                  {t.lab && (
                    <div data-testid="tier-elite-lab" className="mb-5 rounded-xl border border-[#D4AF37]/40 bg-black/50 p-4" style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06), 0 0 18px rgba(212,175,55,0.15)" }}>
                      <div className="flex items-center gap-2 font-mono text-[9px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-3"><ScanLine size={12} /> Technical Academy Lab</div>
                      <ul className="space-y-2.5">
                        {t.lab.map((f) => (
                          <li key={f} className="flex items-start gap-2.5 text-sm text-white/90">
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#D4AF37] shrink-0" style={{ boxShadow: "0 0 8px #D4AF37" }} />
                            <span>{f.includes(" — ") ? <><span className="font-bold text-white">{f.split(" — ")[0]}</span> — {f.split(" — ")[1]}</> : f}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {t.inherits && (
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#C41E24] mb-3">
                      <ArrowRight size={13} /> {t.inherits}
                    </div>
                  )}
                  <ul className="space-y-3">
                    {t.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm text-white/80">
                        <CheckCircle2 size={17} className="text-[#C41E24] shrink-0 mt-0.5" />
                        <span>{f.match(/^\d+% OFF/) ? <><span className="font-bold text-white">{f.split(" ").slice(0, 2).join(" ")}</span> {f.split(" ").slice(2).join(" ")}</> : f}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {t.id === "ebook" ? (
                  <div className="mt-7">
                    <button
                      data-testid="tier-ebook-cta"
                      onClick={getEarlyAccess}
                      disabled={buying}
                      className="w-full flex items-center justify-center gap-2 bg-[#C41E24] hover:bg-[#A0161D] text-white font-subheadings font-bold uppercase tracking-wider py-3.5 rounded-sm transition-colors disabled:opacity-70"
                    >
                      <ShoppingCart size={16} /> {buying ? "Redirecting…" : `Enroll in the Blueprint — $${pricing.retail}`}
                    </button>
                  </div>
                ) : (
                  <div className="mt-7">
                    <button
                      data-testid={`tier-${t.id}-cta`}
                      onClick={() => (membershipsOpen ? buyMembership(t.id) : getEarlyAccess())}
                      disabled={buying}
                      className={`w-full flex items-center justify-center gap-2 font-subheadings font-bold uppercase tracking-wider py-3.5 rounded-sm transition-colors disabled:opacity-70 ${isGold ? "bg-[#D4AF37] hover:bg-[#c69c2e] text-black" : "bg-white/10 hover:bg-white/20 text-white border border-white/20"}`}
                    >
                      {t.highlight ? <Crown size={15} /> : <Sparkles size={15} />}
                      {membershipsOpen
                        ? (buying ? "Redirecting…" : `Enroll in ${t.name} — $${t.id === "elite" ? pricing.elite : pricing.academy}/mo`)
                        : (buying ? "Redirecting…" : `Start with the Blueprint — $${pricing.retail}`)}
                    </button>
                    <p className="text-center text-[11px] text-white/40 mt-2.5 uppercase tracking-wider">
                      {membershipsOpen ? "Cancel anytime · Billed monthly" : "Enrollment is open · start with the Blueprint today"}
                    </p>
                  </div>
                )}
              </motion.div>
              );
            })}
          </div>

          <p className="text-center text-sm text-white/50 mt-12 max-w-2xl mx-auto">
            <span className="text-white font-semibold">A single private session often runs $50–$80.</span> At 20% off, Elite Coaching can pay for itself with just one session a month — plus everything in the Blueprint and the Technical Track.
          </p>
          <div className="mt-10 flex flex-col items-center gap-3">
            <button data-testid="learn-system-btn" onClick={() => navigate("/the-system")}
              className="group inline-flex items-center gap-2.5 border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black font-subheadings font-bold uppercase tracking-wider py-4 px-8 rounded-sm transition-colors text-sm sm:text-base">
              <BookOpen size={18} /> Learn How It Works <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">The Engine · BEOGO · Your Sessions · Progression · Tiers · O-Rank</span>
          </div>
        </div>
      </section>

      {/* FAMILY VALUE — one continuous section straight after the membership cards: whole family → one Blueprint/multiple players → sibling discounts → savings calculator */}
      <div data-testid="family-value-section">
      {/* FAMILY BANNER BAND */}
      <section className="relative overflow-hidden border-y border-[#D4AF37]/40" data-testid="family-banner-section">
        <img loading="lazy" decoding="async" src="/marketing/family-training.jpg" alt="Siblings training together at The Next O Technical Academy" className="absolute inset-0 w-full h-full object-cover object-center" />
        {/* brand grade: carbon left → crimson/gold right */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/85 to-[#0a0a0a]/25" />
        <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(90deg, transparent 45%, rgba(196,30,36,0.28) 100%)", mixBlendMode: "multiply" }} />
        <div className="absolute inset-x-0 top-0 h-1 pointer-events-none" style={{ background: "linear-gradient(90deg, #C41E24, #D4AF37 55%, transparent)" }} />
        <div className="absolute inset-0 grain pointer-events-none opacity-60" />
        {/* HUD brackets + academy stamp (right side, never under the copy) */}
        <div className="hidden md:block absolute top-6 right-6 w-16 h-16 border-t-2 border-r-2 border-[#D4AF37]/80 pointer-events-none" />
        <div className="hidden md:block absolute bottom-6 right-6 w-16 h-16 border-b-2 border-r-2 border-[#D4AF37]/80 pointer-events-none" />
        <div data-testid="family-banner-brand" className="absolute right-5 sm:right-10 bottom-5 sm:bottom-8 flex items-center gap-3 rounded-sm border border-[#D4AF37]/50 bg-[#0a0a0a]/75 backdrop-blur px-3 sm:px-4 py-2 sm:py-2.5" style={{ boxShadow: "0 0 30px rgba(212,175,55,0.25)" }}>
          <img src={ACADEMY_LOGO_H} alt="The Next O Technical Academy" className="h-8 sm:h-10 w-auto object-contain" />
          <div className="hidden sm:block h-8 w-px bg-[#D4AF37]/40" />
          <div className="hidden sm:block font-mono text-[9px] uppercase tracking-[0.3em] text-[#D4AF37] leading-tight">Official<br />Family Training</div>
        </div>
        <div className="hidden lg:flex absolute right-8 top-1/2 -translate-y-1/2 items-center pointer-events-none" style={{ writingMode: "vertical-rl" }}>
          <span className="font-mono text-[10px] uppercase tracking-[0.45em] text-white/35 whitespace-nowrap">The Next O Technical Academy · Est. 2026</span>
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 py-20 sm:py-28">
          <div className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-3 py-1 mb-5 rounded-sm">
            <Users size={14} /><span className="font-subheadings text-[11px] font-bold uppercase tracking-[0.25em]">Family Ready</span>
          </div>
          <h2 className="font-cover text-5xl sm:text-7xl uppercase leading-[0.9] tracking-tight max-w-3xl">
            Training For The <span className="text-[#C41E24]">Whole Family</span>
          </h2>
          <p className="font-subheadings text-lg sm:text-xl text-white/80 mt-5 max-w-xl">
            Got more than one baller at home? <b className="text-white">One purchase covers up to 5 players</b> — each with their own profile, jersey number, journal and leaderboard rank.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-8">
            <button data-testid="family-banner-cta" onClick={getEarlyAccess} disabled={buying}
              className="inline-flex items-center gap-2 bg-[#C41E24] hover:bg-[#A0161D] text-white font-subheadings font-bold uppercase tracking-wider px-6 py-3.5 rounded-sm transition-colors disabled:opacity-70">
              Get It For Your Family <ArrowRight size={16} />
            </button>
            <button data-testid="family-banner-learn" onClick={() => document.querySelector("[data-testid='family-ready-section']")?.scrollIntoView({ behavior: "smooth" })}
              className="inline-flex items-center gap-2 border border-white/25 hover:border-white text-white font-subheadings font-bold uppercase tracking-wider px-6 py-3.5 rounded-sm transition-colors">
              See How It Works
            </button>
          </div>
        </div>
      </section>

      {/* FAMILY READY */}
      <MobileFoldRow id="family-ready" icon={Users} title="One Blueprint. Multiple Players." teaser="One purchase covers every player in your household" accent="#D4AF37" open={!!folds["family-ready"]} onToggle={toggleFold} />
      <section data-fold={fold("family-ready")} className="relative py-24 px-6 sm:px-10 bg-[#0a0a0a]" data-testid="family-ready-section">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.25em] text-[#C41E24] mb-4">
              <Users size={14} /> Family Ready
            </div>
            <h2 className="font-cover text-4xl sm:text-5xl uppercase tracking-tight leading-none mb-4">
              One Blueprint.<br />Multiple Players.
            </h2>
            <p className="text-white/60 text-base leading-relaxed mb-6 max-w-lg">
              Got more than one baller at home? One purchase covers the whole household. Create a separate profile for each child — each with their own name, jersey number, journal, video reviews and leaderboard rank. Jump between them in one tap with <b className="text-white/80">Switch Player</b>.
            </p>
            <ul className="space-y-3 mb-8">
              {[
                "Shared Blueprint access across the whole family",
                "Up to 5 player profiles on one parent account",
                "Each child ranks on the leaderboard on their own",
                "Enroll each child in the Technical Track or Elite for unlimited coach feedback",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-white/75">
                  <CheckCircle2 size={18} className="text-[#C41E24] flex-shrink-0 mt-0.5" /> {t}
                </li>
              ))}
            </ul>
            <button data-testid="family-cta" onClick={getEarlyAccess} disabled={buying}
              className="inline-flex items-center gap-2 bg-[#C41E24] hover:bg-[#A0161D] text-white font-subheadings font-bold uppercase tracking-wider px-6 py-3.5 rounded-sm transition-colors disabled:opacity-70">
              Enroll Your Family in the Blueprint <ArrowRight size={16} />
            </button>
          </div>
          <div className="relative flex justify-center">
            <div className="absolute inset-0 rounded-3xl" style={{ background: "radial-gradient(circle at 50% 40%, rgba(196,30,36,0.25), transparent 65%)" }} />
            <FamilySwitchMock />
          </div>
        </div>
      </section>

      {/* BUILT FOR FAMILIES — sibling discounts */}
      <MobileFoldRow id="siblings" icon={Medal} title="Sibling Discounts" teaser="Extra players enroll at a discount — profiles are free" accent="#D4AF37" open={!!folds["siblings"]} onToggle={toggleFold} />
      <section data-fold={fold("siblings")} className="relative py-24 px-6 sm:px-10 bg-[#0d0d0d] border-y border-white/10" data-testid="built-for-families-section">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#D4AF37] px-3 py-1 mb-5 rounded-full">
            <Users size={14} /><span className="font-subheadings text-[11px] font-bold uppercase tracking-[0.25em]">Built for Families</span>
          </div>
          <h2 className="font-cover text-4xl sm:text-6xl uppercase tracking-tight leading-none mb-4">
            Sibling <span className="text-[#C41E24]">Discounts</span>
          </h2>
          <p className="text-white/60 text-base max-w-2xl mx-auto mb-12">
            Enroll in Volume 1: Ball Mastery Blueprint once and <b className="text-white">every player in your household gets their own profile and leaderboard rank — free.</b> Ready for coaching? Additional siblings enroll in the same track at a discount.
          </p>
          <div className="grid sm:grid-cols-3 gap-5 text-left">
            {[
              { name: "Blueprint", price: "Shared", unit: "/ Free", note: "Every player in the household — free forever", accent: "rgba(255,255,255,0.5)", cls: "border-white/12" },
              { name: "Technical Track", price: "$29", unit: "/mo", sibling: "$19/mo", note: "Unlimited coach video feedback + drills", accent: "#60A5FA", cls: "border-[#60A5FA]/30" },
              { name: "Elite", price: "$49", unit: "/mo", sibling: "$29/mo", note: "Everything in the Technical Track + elite roadmap & priority review", accent: "#D4AF37", cls: "border-[#D4AF37]/50", featured: true },
            ].map((t) => (
              <div key={t.name} data-testid={`family-price-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={`lift-card relative bg-[#141414] border ${t.cls} rounded-xl p-6 ${t.featured ? "sm:-translate-y-2 shadow-[0_20px_60px_rgba(212,175,55,0.12)]" : ""}`}>
                {t.featured && <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-black text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded">Most Popular</span>}
                <div className="text-[11px] font-bold uppercase tracking-[0.25em] mb-3" style={{ color: t.accent }}>{t.name}</div>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="font-cover text-4xl text-white">{t.price}</span>
                  <span className="text-white/40 text-sm">{t.unit}</span>
                </div>
                {t.sibling ? (
                  <div className="inline-flex items-center gap-1.5 bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#D4AF37] text-xs font-bold rounded-full px-2.5 py-1 mb-4">
                    <Users size={11} /> Sibling {t.sibling}
                  </div>
                ) : (
                  <div className="text-xs text-white/40 mb-4">for the whole household</div>
                )}
                <p className="text-sm text-white/60 leading-relaxed">{t.note}</p>
              </div>
            ))}
          </div>
          <p className="text-white/40 text-xs mt-6">Sibling pricing applies automatically at checkout once your household has a member on that plan. All plans are open now.</p>
        </div>
      </section>


      {/* SAVINGS CALCULATOR */}
      <MobileFoldRow id="savings" icon={Calculator} title="Savings Calculator" teaser="See what your enrollment hands back vs. private sessions" open={!!folds["savings"]} onToggle={toggleFold} />
      <section data-fold={fold("savings")} className="relative py-24 px-6 sm:px-10 bg-[#0a0a0a]" data-testid="savings-calculator-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-[#C41E24] font-bold uppercase tracking-[0.3em] text-xs mb-3">Do The Math</div>
          <h2 className="font-headings text-4xl sm:text-5xl uppercase tracking-wide mb-3">Savings Calculator</h2>
          <p className="text-white/60 max-w-2xl mb-12">A single private session runs $50–$80. See how much your Technical Track or Elite enrollment hands back on the training you're <span className="text-white font-semibold">already paying for</span>.</p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            {/* Input */}
            <div className="bg-[#141414] border border-white/10 rounded-2xl p-7">
              <div className="flex items-center gap-2 mb-6"><Calculator size={20} className="text-[#C41E24]" /><h3 className="font-headings text-xl uppercase tracking-wide">Your Training</h3></div>
              <label className="block text-xs font-bold uppercase tracking-widest text-white/55 mb-3">Private sessions per month</label>
              <div className="flex items-center gap-4">
                <input
                  data-testid="calc-sessions-slider"
                  type="range" min="1" max="12" step="1" value={sessions}
                  onChange={(e) => setSessions(Number(e.target.value))}
                  className="flex-1 accent-[#C41E24] cursor-pointer"
                />
                <div className="w-14 text-center">
                  <span data-testid="calc-sessions-value" className="font-cover text-4xl text-[#C41E24] leading-none">{sessions}</span>
                </div>
              </div>
              <div className="mt-6 flex items-center justify-between rounded-lg border border-white/10 bg-[#0d0d0d] px-4 py-3.5">
                <span className="text-sm text-white/60">At ~${SESSION_PRICE}/session, you spend</span>
                <span data-testid="calc-monthly-spend" className="font-cover text-2xl text-white leading-none">${monthlySpend}<span className="text-sm text-white/40 font-sans">/mo</span></span>
              </div>
            </div>

            {/* Output */}
            <div className="space-y-4">
              {/* Academy */}
              <div data-testid="calc-academy-result" className="lift-card bg-[#141414] border border-white/10 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-headings text-lg uppercase tracking-wide">The Technical Track <span className="text-white/40 text-sm">(10% off)</span></span>
                  <span className="text-[11px] font-bold uppercase tracking-wider text-white/45">$29/mo</span>
                </div>
                <div className="flex items-end gap-2">
                  <span className="font-cover text-4xl text-[#22C55E] leading-none">${academyMonthlySave}</span>
                  <span className="text-white/50 text-sm mb-1">saved / month on training</span>
                </div>
                <p className="text-xs text-white/50 mt-2">That's <span className="text-white font-semibold">${academyMonthlySave * 12}/year</span> back{academyNetYear > 0 ? <> — <span className="text-[#22C55E] font-semibold">${academyNetYear} more than the membership costs.</span></> : "."}</p>
              </div>

              {/* Elite */}
              <div data-testid="calc-elite-result" className="rounded-2xl p-6 border-2 border-[#C41E24] bg-gradient-to-b from-[#C41E24]/15 to-[#141414] shadow-[0_16px_50px_rgba(196,30,36,0.25)]">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-headings text-lg uppercase tracking-wide flex items-center gap-1.5"><Crown size={16} className="text-[#F5C518]" /> Elite Coaching <span className="text-white/40 text-sm">(20% off)</span></span>
                  <span className="text-[11px] font-bold uppercase tracking-wider text-white/45">$49/mo</span>
                </div>
                <div className="flex items-end gap-2">
                  <span className="font-cover text-5xl text-[#22C55E] leading-none">${eliteMonthlySave}</span>
                  <span className="text-white/60 text-sm mb-1.5">saved / month on training</span>
                </div>
                <p className="text-sm text-white/70 mt-3 flex items-start gap-1.5">
                  <TrendingUp size={16} className="text-[#22C55E] shrink-0 mt-0.5" />
                  <span>That's <span className="text-white font-bold">${eliteMonthlySave * 12}/year</span> back{eliteNetYear > 0 ? <> — <span className="text-[#22C55E] font-bold">${eliteNetYear} more than the membership costs.</span> It pays for itself.</> : ". Add just one more session to come out ahead."}</span>
                </p>
              </div>
              <p className="text-[11px] text-white/35 leading-relaxed">Estimate based on ~${SESSION_PRICE}/private session. Discounts also apply to group sessions & merch — real savings are typically higher.</p>
            </div>
          </div>
        </div>
      </section>

      </div>

      {/* SEE IT IN ACTION — app tour reel */}
      <MobileFoldRow id="app-tour" icon={PlayCircle} title="See It In Action" teaser="60-second tour · Player Hub, drills, coach feedback" open={!!folds["app-tour"]} onToggle={toggleFold} />
      <section data-fold={fold("app-tour")} className="relative py-20 sm:py-24 px-6 sm:px-10 bg-[#0d0d0d] border-y border-white/10 overflow-hidden" data-testid="app-tour-section">
        <div className="absolute inset-0 red-glow pointer-events-none" />
        <div className="relative max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Copy */}
          <div className="order-2 md:order-1">
            <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.25em] text-[#C41E24] mb-4">
              <PlayCircle size={14} /> See It In Action
            </div>
            <h2 className="font-cover text-4xl sm:text-5xl uppercase tracking-tight leading-[0.9] mb-5">
              Way More Than<br /><span className="text-[#C41E24]">A Training Manual</span>
            </h2>
            <p className="text-white/65 text-base leading-relaxed mb-7 max-w-lg">
              Take the 60-second tour. See the gamified Player Hub, weekly drills, coach video feedback, the monthly roadmap, and one-tap family switching — the full platform in motion.
            </p>
            <ul className="space-y-3 mb-8">
              {[
                "Personalized training plans & weekly drills",
                "1-on-1 coach video & voice feedback",
                "One app for every kid in the house",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-white/80">
                  <CheckCircle2 size={18} className="text-[#C41E24] flex-shrink-0 mt-0.5" /> {t}
                </li>
              ))}
            </ul>
            <button data-testid="app-tour-cta" onClick={getEarlyAccess} disabled={buying}
              className="cta-glow group inline-flex items-center gap-2 bg-[#C41E24] hover:bg-[#A0161D] text-white font-subheadings font-bold uppercase tracking-wider px-7 py-3.5 rounded-full transition-colors disabled:opacity-70">
              {buying ? "Redirecting…" : earlyOpen ? `Get Early Access — $${pricing.early}` : `Enroll in the Blueprint — $${pricing.retail}`}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          {/* Storyboard carousel */}
          <div className="order-1 md:order-2 flex justify-center">
            <div className="relative w-full max-w-[320px]">
              <div className="absolute -inset-6 rounded-[2.5rem] pointer-events-none" style={{ background: "radial-gradient(circle at 50% 40%, rgba(196,30,36,0.3), transparent 68%)" }} />
              <div className="relative rounded-3xl overflow-hidden border border-white/12 bg-black shadow-[0_40px_100px_rgba(0,0,0,0.7)]" data-testid="tour-carousel">
                <div className="relative aspect-[9/16]">
                  {KEYFRAMES.map((k, i) => (
                    <img loading="lazy" decoding="async" key={k.src} src={k.src} alt={k.phase} data-testid={`tour-slide-${i}`}
                      className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === slide ? "opacity-100" : "opacity-0"}`} />
                  ))}
                  {/* phase pill (top) — keeps the baked-in frame text fully visible */}
                  <div className="absolute top-3 left-3 z-10 flex items-center gap-1.5 bg-black/55 backdrop-blur border border-white/15 rounded-full px-3 py-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C41E24]" />
                    <span data-testid="tour-phase" className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/90">{KEYFRAMES[slide].phase}</span>
                  </div>
                  {/* prev / next */}
                  <button data-testid="tour-prev" aria-label="Previous" onClick={() => setSlide((s) => (s - 1 + KEYFRAMES.length) % KEYFRAMES.length)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 hover:bg-black/80 backdrop-blur border border-white/15 flex items-center justify-center text-white/80 hover:text-white transition-colors">
                    <ArrowRight size={16} className="rotate-180" />
                  </button>
                  <button data-testid="tour-next" aria-label="Next" onClick={() => setSlide((s) => (s + 1) % KEYFRAMES.length)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 hover:bg-black/80 backdrop-blur border border-white/15 flex items-center justify-center text-white/80 hover:text-white transition-colors">
                    <ArrowRight size={16} />
                  </button>
                </div>
              </div>
              {/* dots */}
              <div className="flex items-center justify-center gap-2 mt-5">
                {KEYFRAMES.map((k, i) => (
                  <button key={k.src} data-testid={`tour-dot-${i}`} aria-label={`Go to ${k.phase}`} onClick={() => setSlide(i)}
                    className={`h-2 rounded-full transition-all ${i === slide ? "w-7 bg-[#C41E24]" : "w-2 bg-white/25 hover:bg-white/50"}`} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INSIDE THE LAB — Diamond product visuals */}
      <LabShowcase onCta={scrollToTiers} ctaLabel="See the Academy Tiers" disabled={false} />

      {/* MEET YOUR COACH */}
      <MobileFoldRow id="meet-coach" icon={Star} title="Meet Coach Barker" teaser="The technical director behind the Next O Standard" open={!!folds["meet-coach"]} onToggle={toggleFold} />
      <section data-fold={fold("meet-coach")} className="relative py-20 sm:py-24 px-6 sm:px-10 bg-[#0a0a0a]" data-testid="meet-coach-section">
        <div className="absolute inset-0 red-glow pointer-events-none" />
        <div className="relative max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="relative">
            <div className="absolute -inset-4 rounded-3xl" style={{ background: "radial-gradient(circle at 60% 40%, rgba(196,30,36,0.28), transparent 68%)" }} />
            <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.6)]">
              <img loading="lazy" decoding="async" src={COACH_IMG} alt="Coach Dwain Barker" data-testid="coach-portrait"
                className="w-full h-full object-cover aspect-[4/5]" />
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/85 to-transparent p-5">
                <div className="font-cover text-2xl uppercase tracking-wide leading-none">Coach Dwain Barker</div>
                <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#C41E24] mt-1">Founder · Next O Soccer</div>
              </div>
            </div>
          </div>
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.25em] text-[#D4AF37] mb-4">
              <Crown size={14} /> Meet Your Coach
            </div>
            <h2 className="font-cover text-4xl sm:text-5xl uppercase tracking-tight leading-[0.9] mb-5">
              Real Coaching.<br /><span className="text-[#C41E24]">Real Results.</span>
            </h2>
            <p className="text-white/65 text-base leading-relaxed mb-6 max-w-lg">
              This isn't a faceless PDF. Every drill, standard and progression in the system was built by a coach who's spent years developing technical players — and now personally reviews your reps and leaves feedback inside the app.
            </p>
            <ul className="space-y-3 mb-7">
              {[
                "A proven, chapter-by-chapter technical curriculum",
                "Personal video feedback & voice notes on your uploads",
                "A gamified Hub that keeps kids training without nagging",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-white/80">
                  <CheckCircle2 size={18} className="text-[#C41E24] flex-shrink-0 mt-0.5" /> {t}
                </li>
              ))}
            </ul>
            <button data-testid="coach-cta" onClick={getEarlyAccess} disabled={buying}
              className="cta-glow group inline-flex items-center gap-2 bg-[#C41E24] hover:bg-[#A0161D] text-white font-subheadings font-bold uppercase tracking-wider px-7 py-3.5 rounded-full transition-colors disabled:opacity-70">
              {buying ? "Redirecting…" : "Train With Coach Barker"} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>

      {/* SNEAK PEEK */}
      <MobileFoldRow id="sneak-peek" icon={Video} title="Sneak Peek" teaser="Video proof, coach feedback & the live leaderboard" open={!!folds["sneak-peek"]} onToggle={toggleFold} />
      <section data-fold={fold("sneak-peek")} className="relative py-24 px-6 sm:px-10 max-w-6xl mx-auto" data-testid="sneak-peek-section">
        <div className="absolute inset-0 red-glow pointer-events-none" />
        <div className="relative">
          <div className="text-[#C41E24] font-bold uppercase tracking-[0.3em] text-xs mb-3">A First Look</div>
          <h2 className="font-headings text-4xl sm:text-5xl uppercase tracking-wide mb-3">Sneak Peek</h2>
          <p className="text-white/60 max-w-2xl mb-12">Here's what's dropping — record your reps, get coach feedback, and climb the Central Illinois leaderboard.</p>

          {/* Player of the Week */}
          <div className="max-w-xl mb-14"><PlayerOfWeek sampleFallback /></div>

          {/* Preview video */}
          <div className="mb-14">
            <div className="inline-flex items-center gap-2 mb-4"><PlayCircle size={18} className="text-[#C41E24]" /><span className="font-subheadings text-sm font-bold uppercase tracking-widest text-white/70">Watch The Preview</span></div>
            <div className="rounded-xl overflow-hidden border border-white/10 aspect-video shadow-[0_16px_48px_rgba(0,0,0,0.6)] max-w-3xl">
              {embed ? (
                <iframe className="w-full h-full" src={embed} title="Next O Soccer sneak peek" frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-testid="sneak-peek-video" />
              ) : (
                <a href={SNEAK_VIDEO} target="_blank" rel="noopener noreferrer" className="w-full h-full flex items-center justify-center bg-[#141414]">Watch</a>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Video proof mock-up */}
            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
              data-testid="mockup-video-proof" className="lift-card bg-[#141414] border border-white/10 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4"><Video size={18} className="text-[#C41E24]" /><h3 className="font-headings text-xl uppercase tracking-wide">Video Proof & Coach Feedback</h3></div>
              <p className="text-sm text-white/60 mb-5">Record a rep in the app, upload it in seconds, and Coach Barker sends back a personal reaction — an emoji, a voice note, and written notes — so your player always knows exactly what to fix next.</p>
              {/* fake app card */}
              <div className="bg-[#0d0d0d] border border-white/10 rounded-xl overflow-hidden">
                <div className="relative aspect-video bg-gradient-to-br from-[#1c1c1c] to-[#0a0a0a] flex items-center justify-center">
                  <PlayCircle size={54} className="text-white/25" />
                  <span className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wider bg-black/60 px-2 py-1 rounded">Elastico · 0:14</span>
                  <span className="absolute top-3 right-3 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#22C55E] bg-[#22C55E]/15 px-2 py-1 rounded"><CheckCircle2 size={11} /> Approved</span>
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-white/45">Coach Reaction</span>
                    <span className="ml-auto text-[9px] font-bold uppercase tracking-wider text-[#C41E24] bg-[#C41E24]/15 px-2 py-0.5 rounded-full">New!</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-3xl leading-none">🔥</span>
                    <div className="flex-1 flex items-center gap-2 bg-[#141414] border border-white/10 rounded-md px-3 py-2">
                      <PlayCircle size={16} className="text-[#C41E24]" />
                      <div className="flex-1 flex items-center gap-[2px] h-5">
                        {[6, 12, 8, 16, 10, 18, 9, 14, 7, 13, 6, 11].map((h, i) => (
                          <span key={i} className="w-[3px] rounded-full bg-[#C41E24]/70" style={{ height: `${h}px` }} />
                        ))}
                      </div>
                      <span className="text-[10px] text-white/40">0:12</span>
                    </div>
                  </div>
                  <p className="text-xs text-white/55 mt-3 italic">"Clean touch, Marcus — keep your head up on the exit. 💪"</p>
                </div>
              </div>
              {/* User-journey tour reel — silent loop, shown alongside the description */}
              <div className="mt-6">
                <div className="inline-flex items-center gap-2 mb-3"><PlayCircle size={16} className="text-[#C41E24]" /><span className="font-subheadings text-xs font-bold uppercase tracking-widest text-white/60">Take the Elite Onscreen Tour</span></div>
                <div className="relative flex justify-center">
                  <div className="absolute -inset-4 rounded-[2rem] pointer-events-none" style={{ background: "radial-gradient(circle at 50% 40%, rgba(196,30,36,0.25), transparent 68%)" }} />
                  <div className="relative w-full max-w-[240px] rounded-3xl overflow-hidden border border-white/12 bg-black shadow-[0_30px_80px_rgba(0,0,0,0.7)]">
                    <video ref={tourVideoRef} data-testid="elite-tour-video" src="/marketing/app-tour-2026.mp4" poster="/marketing/app-tour-2026-poster.jpg"
                      autoPlay muted loop playsInline preload="metadata" className="w-full h-full object-cover block aspect-[9/16]" />
                    <button
                      data-testid="elite-tour-sound-toggle"
                      onClick={() => {
                        tourOverrideRef.current = true;
                        const v = tourVideoRef.current;
                        const nextMuted = !tourMuted;
                        setTourMuted(nextMuted);
                        if (v) {
                          v.muted = nextMuted;
                          if (!nextMuted) { v.volume = 1; v.currentTime = 0; }
                          v.play?.().catch(() => {});
                        }
                      }}
                      aria-label={tourMuted ? "Unmute tour voiceover" : "Mute tour voiceover"}
                      className="absolute bottom-3 right-3 z-10 flex items-center gap-1.5 bg-black/70 hover:bg-black/85 border border-[#F5C518]/50 text-[#F5C518] text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-full backdrop-blur-sm transition-colors">
                      {tourMuted ? <><VolumeX size={13} /> Tap for Sound</> : <><Volume2 size={13} /> Sound On</>}
                    </button>
                  </div>
                </div>
                <p className="text-xs text-white/40 mt-4 flex items-center gap-1.5 justify-center"><Sparkles size={12} className="text-[#C41E24]" /> The real journey — Hub → Chapter → Upload → Leaderboard</p>
              </div>
            </motion.div>

            {/* Leaderboard — live Central Illinois rankings straight from the database (top 10, auto-refresh) */}
            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.08 }} data-testid="mockup-leaderboard">
              <LiveLeaderboardPreview onEnroll={scrollToTiers} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHY NEXT O */}
      <MobileFoldRow id="why" icon={Sparkles} title="Why Next O Soccer?" teaser="Real progress tracking, elite methods, coach access" open={!!folds["why"]} onToggle={toggleFold} />
      <section data-fold={fold("why")} className="relative py-24 px-6 sm:px-10 border-y border-white/10 overflow-hidden" data-testid="why-next-o-section">
        <img loading="lazy" decoding="async" src={SECTION_BG_WHY} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-[#0d0d0d]/85" />
        <div className="relative max-w-6xl mx-auto">
          <div className="text-[#C41E24] font-bold uppercase tracking-[0.3em] text-xs mb-3">The Difference</div>
          <h2 className="font-headings text-4xl sm:text-5xl uppercase tracking-wide mb-12">Why Next O Soccer?</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Progress charts */}
            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
              data-testid="why-charts" className="lift-card bg-[#141414] border border-white/10 rounded-2xl p-6">
              <BarChart3 size={26} className="text-[#C41E24] mb-4" />
              <h3 className="font-headings text-xl uppercase tracking-wide mb-2">Track Progress Visually</h3>
              <p className="text-sm text-white/55 mb-5">Every session logged, every chapter charted. Kids (and parents) see real improvement week over week.</p>
              <div className="flex items-end gap-2 h-24 bg-[#0d0d0d] border border-white/10 rounded-lg p-3">
                {[38, 52, 45, 63, 71, 80, 92].map((h, i) => (
                  <div key={i} className="flex-1 bg-[#C41E24]/80 rounded-t" style={{ height: `${h}%` }} />
                ))}
              </div>
            </motion.div>

            {/* Expert how-to videos (embed) */}
            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.08 }}
              data-testid="why-videos" className="lift-card bg-[#141414] border border-white/10 rounded-2xl p-6">
              <PlayCircle size={26} className="text-[#C41E24] mb-4" />
              <h3 className="font-headings text-xl uppercase tracking-wide mb-2">Expert How-To Videos</h3>
              <p className="text-sm text-white/55 mb-5">Watch the exact technique before you train it — no guesswork, just pro-level demonstration.</p>
              <div className="rounded-lg overflow-hidden border border-white/10 aspect-video">
                {embed ? (
                  <iframe className="w-full h-full" src={embed} title="Expert how-to video" frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-testid="why-videos-embed" />
                ) : (
                  <a href={SNEAK_VIDEO} target="_blank" rel="noopener noreferrer" className="w-full h-full flex items-center justify-center bg-[#0d0d0d]">Watch</a>
                )}
              </div>
            </motion.div>

            {/* Gamified leaderboard */}
            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.16 }}
              data-testid="why-leaderboard" className="lift-card bg-[#141414] border border-white/10 rounded-2xl p-6">
              <Trophy size={26} className="text-[#C41E24] mb-4" />
              <h3 className="font-headings text-xl uppercase tracking-wide mb-2">Motivation Built In</h3>
              <p className="text-sm text-white/55 mb-5">The gamified leaderboard keeps kids hungry to practice — so parents don't have to be the ones pushing.</p>
              <div className="bg-[#0d0d0d] border border-white/10 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm"><Medal size={18} style={{ color: MEDAL_COLORS[1] }} /> <span className="font-semibold">Earn points every session</span></div>
                <div className="flex items-center gap-2 text-sm"><Flame size={18} className="text-[#C41E24]" /> <span className="font-semibold">Build streaks, climb ranks</span></div>
                <div className="flex items-center gap-2 text-sm"><Star size={18} className="text-[#F5C518]" /> <span className="font-semibold">Compete with players across Central Illinois</span></div>
              </div>
            </motion.div>
          </div>

          {/* Closing CTA */}
          <div className="mt-14 flex flex-col sm:flex-row items-center gap-4 justify-center text-center">
            <button data-testid="why-cta-btn" onClick={getEarlyAccess} disabled={buying}
              className="cta-glow group flex items-center gap-3 bg-[#C41E24] hover:bg-[#A0161D] text-white font-subheadings font-bold uppercase tracking-wider py-4 px-8 rounded-full transition-colors disabled:opacity-70">
              {buying ? "Redirecting…" : earlyOpen ? `Lock In Early Access — $${pricing.early}` : `Enroll in the Blueprint — $${pricing.retail}`}
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
            {earlyOpen && <span className="text-white/45 text-sm">Price rises to ${pricing.retail} at launch.</span>}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <TestimonialSection />

      {/* FAQ */}
      <section className="relative py-24 px-6 sm:px-10 bg-[#0a0a0a]" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.25em] text-[#C41E24] mb-3">
              <HelpCircle size={14} /> Frequently Asked Questions
            </div>
            <h2 className="font-cover text-4xl sm:text-5xl uppercase tracking-tight">Got Questions?</h2>
          </div>
          <div className="space-y-3">
            {FAQS.map((f, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-[#141414] overflow-hidden">
                <button data-testid={`faq-q-${i}`} onClick={() => setFaqOpen(faqOpen === i ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left">
                  <span className="text-base font-semibold text-white/90">{f.q}</span>
                  <ChevronDown size={18} className={`text-[#C41E24] shrink-0 transition-transform ${faqOpen === i ? "rotate-180" : ""}`} />
                </button>
                {faqOpen === i && (
                  <p data-testid={`faq-a-${i}`} className="px-5 pb-5 -mt-1 text-sm text-white/60 leading-relaxed">{f.a}</p>
                )}
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <button data-testid="faq-cta" onClick={getEarlyAccess} disabled={buying}
              className="cta-glow group inline-flex items-center gap-2 bg-[#C41E24] hover:bg-[#A0161D] text-white font-subheadings font-bold uppercase tracking-wider px-8 py-4 rounded-full transition-colors disabled:opacity-70">
              <ShoppingCart size={16} /> {buying ? "Redirecting…" : `Enroll in the Blueprint — $${pricing.retail}`}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <p className="text-white/45 text-sm mt-4">Club or school player? <button data-testid="faq-redeem-link" onClick={() => { setRedeemCode(""); setShowRedeem(true); }} className="text-[#D4AF37] font-semibold hover:underline">Redeem your team code →</button></p>
          </div>
        </div>
      </section>

      <footer className="py-10 px-6 text-center">
        <img src={ACADEMY_LOGO} alt="The Next O Technical Academy" className="w-36 h-auto object-contain mx-auto mb-3" />
        <p className="text-xs text-white/40 uppercase tracking-widest">© 2026 The Next O Technical Academy · Elite Technical Training System · Volume 1: Ball Mastery Blueprint</p>
        <div className="mt-3 flex items-center justify-center gap-3 flex-wrap">
          <button data-testid="footer-for-schools-link" onClick={() => navigate("/for-schools")} className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-[#D4AF37]/70 hover:text-[#D4AF37] transition-colors">
            <GraduationCap size={12} /> For Schools &amp; Colleges
          </button>
          <button data-testid="coach-mode-link" onClick={() => navigate("/coach")} className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-white/30 hover:text-white transition-colors">
            <Lock size={11} /> Coach Mode
          </button>
        </div>
        <div className="mt-2 flex items-center justify-center gap-1 flex-wrap text-[11px] font-bold uppercase tracking-wider text-white/35">
          <button data-testid="footer-terms-link" onClick={() => navigate("/terms")} className="px-3 py-2 hover:text-white transition-colors">Terms of Service</button>
          <span className="text-white/20">·</span>
          <button data-testid="footer-privacy-link" onClick={() => navigate("/privacy")} className="px-3 py-2 hover:text-white transition-colors">Privacy Policy</button>
          <span className="text-white/20">·</span>
          <a data-testid="footer-contact-link" href="mailto:info@nextosoccer.com" className="px-3 py-2 hover:text-white transition-colors">info@nextosoccer.com</a>
        </div>
      </footer>

      <AuthDialog open={showAuth} onClose={() => setShowAuth(false)} onSuccess={() => navigate("/")} />
      <RedeemDialog open={showRedeem} onClose={() => setShowRedeem(false)} initialCode={redeemCode} onSuccess={() => navigate("/")} />
    </div>
  );
}
